import React, { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { ScanLine, QrCode, Tag as TagIcon, Download, Printer, RotateCcw, Sparkles, Save, FileSpreadsheet, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import BulkTagImport from "@/components/BulkTagImport";
import SellFromScan from "@/components/SellFromScan";
import { api } from "@/lib/api";

// -----------------------------------------------------------------------------
// TagGenerator — a jewellery / SKU tag with an embedded QR that our own
// BarcodeScanner recognises (`kind: "f2n_tag"`). Users can:
//   • Scan an existing tag → fields auto-populate.
//   • Fill the form → live QR preview + download PNG / print.
//
// The QR encodes a compact JSON payload so scanning restores every field.
// Total value is auto-derived from `gold_weight × cost_per_unit × quantity`,
// with optional making-charge & GST adjustments the user can toggle in.
// -----------------------------------------------------------------------------
const PURITIES = ["9K", "14K", "18K", "20K", "22K", "24K", "925 Silver", "999 Silver", "Platinum"];
const WEIGHT_UNITS = ["g", "mg", "kg", "ct", "tola", "oz"];

const emptyTag = () => ({
  item_code:      "",
  metal:          "Gold",
  purity:         "22K",
  gross_weight:   "",
  net_weight:     "",
  diamond_weight: "",
  stone_weight:   "",
  gold_weight:    "",
  unit:           "g",
  quantity:       "1",
  cost_per_unit:  "",
  making_pct:     "0",
  gst_pct:        "3",
  hallmark:       "",
  notes:          "",
});

const num = (v) => {
  const n = parseFloat(v);
  return isFinite(n) ? n : 0;
};

const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// A shorter JSON serialiser — drop empty fields so the QR stays scannable
// even at 25 mm print size.
const compactPayload = (t) => {
  const clean = { kind: "f2n_tag" };
  Object.entries(t).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) clean[k] = v;
  });
  return clean;
};

export default function TagGenerator() {
  const [tag, setTag] = useState(emptyTag());
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sellTag, setSellTag] = useState(null);        // scanned tag → open Sell modal
  const canvasWrapRef = useRef(null);
  const printRef = useRef(null);

  const set = (patch) => setTag((t) => ({ ...t, ...patch }));

  // Live totals — pure math, no side effects.
  const totals = useMemo(() => {
    const qty     = Math.max(1, num(tag.quantity) || 1);
    const gWeight = num(tag.gold_weight) || num(tag.net_weight);
    const cpu     = num(tag.cost_per_unit);
    const metal   = gWeight * cpu;
    const making  = metal * (num(tag.making_pct) / 100);
    const subtotal = (metal + making) * qty;
    const gst     = subtotal * (num(tag.gst_pct) / 100);
    return {
      metal_value:   metal,
      making_value:  making,
      subtotal:      subtotal,
      gst_value:     gst,
      grand_total:   subtotal + gst,
      quantity:      qty,
    };
  }, [tag]);

  const qrValue = useMemo(() => {
    const payload = { ...compactPayload(tag), totals: { grand: Math.round(totals.grand_total) } };
    return JSON.stringify(payload);
  }, [tag, totals.grand_total]);

  const handleScanned = (data) => {
    if (data?.is_tag && data?.tag) {
      setTag((t) => ({ ...t, ...emptyTag(), ...data.tag }));
      toast.success(`Loaded tag ${data.tag.item_code || ""}`);
      // Immediately offer to sell the scanned item.
      setSellTag(data.tag);
    } else if (data?.is_qr) {
      toast.message("Scanned QR isn't a find2near tag — showing raw content.", { description: data.code });
    }
  };

  const saveTag = async () => {
    if (!tag.item_code.trim()) { toast.error("Item code is required to save"); return; }
    setSaving(true);
    try {
      // Send only fields the backend expects — everything else stays local.
      const payload = {
        ...tag,
        quantity: parseInt(tag.quantity || 1, 10) || 1,
        gross_weight:   tag.gross_weight   === "" ? null : parseFloat(tag.gross_weight),
        net_weight:     tag.net_weight     === "" ? null : parseFloat(tag.net_weight),
        gold_weight:    tag.gold_weight    === "" ? null : parseFloat(tag.gold_weight),
        diamond_weight: tag.diamond_weight === "" ? null : parseFloat(tag.diamond_weight),
        stone_weight:   tag.stone_weight   === "" ? null : parseFloat(tag.stone_weight),
        cost_per_unit:  tag.cost_per_unit  === "" ? null : parseFloat(tag.cost_per_unit),
        making_pct:     parseFloat(tag.making_pct || 0),
        gst_pct:        parseFloat(tag.gst_pct || 0),
      };
      const r = await api.post("/erp/tags", payload);
      toast.success(`Saved · ${r.data.item_code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = () => setTag(emptyTag());

  const downloadPng = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `tag-${tag.item_code || "new"}.png`;
    a.click();
  };

  const printTag = () => {
    if (!printRef.current) return;
    const html = printRef.current.innerHTML;
    const w = window.open("", "_blank", "width=420,height=560");
    if (!w) { toast.error("Popup blocked — allow popups to print."); return; }
    w.document.write(`<!doctype html><html><head><title>${tag.item_code || "Tag"}</title>
      <style>body{font-family:system-ui,-apple-system,sans-serif;padding:12px}
        .tag{border:1px dashed #999;border-radius:8px;padding:10px;width:280px}
        .row{display:flex;justify-content:space-between;font-size:11px;padding:1px 0}
        .code{font-family:'JetBrains Mono',monospace;font-weight:800;font-size:14px}
        .center{text-align:center}</style></head><body>${html}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 250);
  };

  return (
    <div className="bg-white border border-stone-200 rounded-3xl overflow-hidden" data-testid="tag-generator">
      <BarcodeScanner open={scanning} onClose={() => setScanning(false)} onScanned={handleScanned} mode="any" />
      <BulkTagImport open={bulkOpen} onClose={() => setBulkOpen(false)} onImported={() => { /* list refresh could go here */ }} />
      <SellFromScan open={!!sellTag} tag={sellTag} onClose={() => setSellTag(null)} />

      <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-gradient-to-r from-amber-50 to-yellow-50 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <TagIcon className="text-amber-700" size={16} />
          <div className="font-black text-stone-900">Product Tag · QR Generator</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" onClick={() => setScanning(true)} variant="outline"
                  className="rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-8 px-3"
                  data-testid="tag-scan-btn">
            <ScanLine size={14} className="mr-1.5" /> Scan QR / Barcode
          </Button>
          <Button type="button" onClick={saveTag} disabled={saving}
                  className="rounded-full h-8 px-3 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="tag-save-btn">
            {saving ? <Loader2 className="animate-spin mr-1" size={13} /> : <Save size={13} className="mr-1" />}
            Save
          </Button>
          <Button type="button" onClick={() => setBulkOpen(true)} variant="outline"
                  className="rounded-full h-8 px-3 border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="tag-bulk-btn">
            <FileSpreadsheet size={13} className="mr-1" /> Bulk CSV
          </Button>
          <Button type="button" onClick={reset} variant="ghost"
                  className="rounded-full h-8 px-3 text-stone-600 hover:bg-stone-50"
                  data-testid="tag-reset-btn">
            <RotateCcw size={13} className="mr-1" /> Reset
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_260px] gap-5 p-5">
        {/* ---------------- Form ---------------- */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Field label="Item code" testid="tag-item-code" placeholder="IT LR118">
              <Input value={tag.item_code} onChange={(e) => set({ item_code: e.target.value.toUpperCase() })}
                     className="rounded-xl font-mono" data-testid="tag-item-code" />
            </Field>
            <Field label="Metal">
              <Select value={tag.metal} onValueChange={(v) => set({ metal: v })}>
                <SelectTrigger className="rounded-xl" data-testid="tag-metal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Gold", "Silver", "Platinum", "Diamond", "Other"].map((m) =>
                    <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Purity">
              <Select value={tag.purity} onValueChange={(v) => set({ purity: v })}>
                <SelectTrigger className="rounded-xl" data-testid="tag-purity"><SelectValue /></SelectTrigger>
                <SelectContent>{PURITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label={`Gross wt (${tag.unit})`}>
              <Input type="number" step="0.001" value={tag.gross_weight}
                     onChange={(e) => set({ gross_weight: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-gross" placeholder="1.950" />
            </Field>
            <Field label={`Net wt (${tag.unit})`}>
              <Input type="number" step="0.001" value={tag.net_weight}
                     onChange={(e) => set({ net_weight: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-net" placeholder="1.950" />
            </Field>
            <Field label={`Gold wt (${tag.unit})`}>
              <Input type="number" step="0.001" value={tag.gold_weight}
                     onChange={(e) => set({ gold_weight: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-gold" placeholder="3.453" />
            </Field>
            <Field label="Unit">
              <Select value={tag.unit} onValueChange={(v) => set({ unit: v })}>
                <SelectTrigger className="rounded-xl" data-testid="tag-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{WEIGHT_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Diamond wt (ct)">
              <Input type="number" step="0.001" value={tag.diamond_weight}
                     onChange={(e) => set({ diamond_weight: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-diamond" placeholder="0.000" />
            </Field>
            <Field label="Stone wt">
              <Input type="number" step="0.001" value={tag.stone_weight}
                     onChange={(e) => set({ stone_weight: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-stone" placeholder="0.000" />
            </Field>
            <Field label="Quantity">
              <Input type="number" min="1" step="1" value={tag.quantity}
                     onChange={(e) => set({ quantity: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-qty" placeholder="1" />
            </Field>
            <Field label={`Cost / ${tag.unit} (₹)`}>
              <Input type="number" step="1" value={tag.cost_per_unit}
                     onChange={(e) => set({ cost_per_unit: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-cpu" placeholder="7000" />
            </Field>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Making %">
              <Input type="number" step="0.1" value={tag.making_pct}
                     onChange={(e) => set({ making_pct: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-making" placeholder="0" />
            </Field>
            <Field label="GST %">
              <Input type="number" step="0.1" value={tag.gst_pct}
                     onChange={(e) => set({ gst_pct: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-gst" placeholder="3" />
            </Field>
            <Field label="Hallmark / BIS">
              <Input value={tag.hallmark} onChange={(e) => set({ hallmark: e.target.value })}
                     className="rounded-xl font-mono" data-testid="tag-hallmark" placeholder="HUID-1234" />
            </Field>
            <Field label="Notes">
              <Input value={tag.notes} onChange={(e) => set({ notes: e.target.value })}
                     className="rounded-xl" data-testid="tag-notes" placeholder="Ring, size 12" />
            </Field>
          </div>

          {/* Live totals strip */}
          <div className="grid grid-cols-4 gap-2 mt-1" data-testid="tag-totals">
            <Kpi label="Metal value" value={money(totals.metal_value)} />
            <Kpi label="Making" value={money(totals.making_value)} />
            <Kpi label={`Qty × Subtotal`} value={`${totals.quantity} × ${money(totals.subtotal / totals.quantity)}`} />
            <Kpi label="Total (incl. GST)" value={money(totals.grand_total)} accent />
          </div>
        </div>

        {/* ---------------- QR + printable tag ---------------- */}
        <div className="space-y-3">
          <div className="rounded-2xl border border-stone-200 p-3 bg-stone-50" ref={canvasWrapRef}>
            <div className="flex items-center justify-center bg-white rounded-xl p-3">
              <QRCodeCanvas value={qrValue} size={196} level="M" includeMargin data-testid="tag-qr" />
            </div>
            <div className="mt-2 text-[10px] text-stone-500 text-center">
              QR encodes the full tag payload · scan with the built-in scanner to auto-load
            </div>
          </div>

          <div ref={printRef}>
            <div className="tag rounded-2xl border-2 border-dashed border-amber-400 p-3 text-xs bg-white">
              <div className="center text-[10px] uppercase tracking-widest font-black text-amber-700">find2near · Product Tag</div>
              <div className="center code text-stone-900 my-1">{tag.item_code || "—"} · {tag.purity}</div>
              <div className="row"><span>Gross</span><span className="font-mono">{tag.gross_weight || "—"} {tag.unit}</span></div>
              <div className="row"><span>Net</span><span className="font-mono">{tag.net_weight || "—"} {tag.unit}</span></div>
              {num(tag.diamond_weight) > 0 && <div className="row"><span>Diamond</span><span className="font-mono">{tag.diamond_weight} ct</span></div>}
              {num(tag.stone_weight) > 0 && <div className="row"><span>Stone</span><span className="font-mono">{tag.stone_weight} {tag.unit}</span></div>}
              <div className="row"><span>Qty × Rate</span><span className="font-mono">{totals.quantity} × ₹{num(tag.cost_per_unit).toLocaleString("en-IN")}/{tag.unit}</span></div>
              <div className="row" style={{ borderTop: "1px solid #eee", marginTop: 4, paddingTop: 4 }}>
                <span style={{ fontWeight: 800 }}>Total</span>
                <span className="font-mono" style={{ fontWeight: 800 }}>{money(totals.grand_total)}</span>
              </div>
              {tag.hallmark && <div className="row"><span>Hallmark</span><span className="font-mono">{tag.hallmark}</span></div>}
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" onClick={downloadPng} className="flex-1 rounded-full bg-stone-900 hover:bg-stone-800" data-testid="tag-download">
              <Download size={14} className="mr-1.5" /> PNG
            </Button>
            <Button type="button" onClick={printTag} className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700" data-testid="tag-print">
              <Printer size={14} className="mr-1.5" /> Print
            </Button>
          </div>
          <div className="text-[10px] text-stone-500 text-center flex items-center justify-center gap-1">
            <Sparkles size={10} className="text-fuchsia-500" /> Compatible with the same scanner above
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-0.5">{label}</div>
      {children}
    </label>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-2 ${accent ? "bg-emerald-50 border-emerald-200" : "bg-white border-stone-200"}`}>
      <div className="text-[9px] uppercase tracking-widest font-bold text-stone-500">{label}</div>
      <div className={`font-mono font-black text-sm ${accent ? "text-emerald-800" : "text-stone-900"}`}>{value}</div>
    </div>
  );
}
